import { jsPDF } from "jspdf";
import { copy } from "./i18n";

const sanitize = (s) =>
  String(s)
    .replace(/≈/g, "~")
    .replace(/−/g, "-")
    .replace(/—|–/g, "-")
    .replace(/→/g, "->")
    .replace(/€/g, "EUR ")
    .replace(/…/g, "...")
    .replace(/×/g, "x");

const loadImg = async (src) => {
  const res = await fetch(src);
  const blob = await res.blob();
  return await new Promise((resolve) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.readAsDataURL(blob);
  });
};

const imgFmt = (dataUrl) => (dataUrl.startsWith("data:image/jpeg") ? "JPEG" : "PNG");

export const generateDossier = async (lang) => {
  // jsPDF core fonts cannot shape Arabic script: fall back to English content
  const t = copy[lang === "ar" ? "en" : lang];
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210;
  const M = 15;
  let y = 20;
  const RED = [255, 42, 0];

  const checkPage = (need) => {
    if (y + need > 278) {
      doc.addPage();
      y = 20;
    }
  };
  const heading = (txt) => {
    checkPage(22);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(15, 15, 15);
    doc.text(sanitize(txt).toUpperCase(), M, y);
    doc.setDrawColor(...RED);
    doc.setLineWidth(0.8);
    doc.line(M, y + 2, M + 22, y + 2);
    y += 11;
  };
  const sub = (txt) => {
    checkPage(14);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(20, 20, 20);
    doc.text(sanitize(txt), M, y);
    y += 6;
  };
  const para = (txt, size = 9, color = [90, 95, 103]) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(sanitize(txt), W - 2 * M);
    checkPage(lines.length * 4.4 + 4);
    doc.text(lines, M, y);
    y += lines.length * 4.4 + 3;
  };
  const bullets = (items) => items.forEach((it) => para("+  " + it, 9));
  const image = async (src, h) => {
    checkPage(h + 8);
    const data = await loadImg(src);
    doc.addImage(data, imgFmt(data), M, y, W - 2 * M, h);
    y += h + 8;
  };
  const table = (head, rows, widths) => {
    const total = widths.reduce((a, b) => a + b, 0);
    const rowH = 7;
    checkPage(rowH * (rows.length + 1) + 8);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...RED);
    let x = M;
    head.forEach((h, i) => {
      doc.text(sanitize(h).toUpperCase(), x + 1, y + 5);
      x += widths[i];
    });
    y += rowH;
    doc.setDrawColor(200, 200, 200);
    doc.line(M, y, M + total, y);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 64, 70);
    rows.forEach((r) => {
      checkPage(rowH + 4);
      x = M;
      r.forEach((c, i) => {
        doc.text(sanitize(String(c)), x + 1, y + 5);
        x += widths[i];
      });
      y += rowH;
      doc.line(M, y, M + total, y);
    });
    y += 7;
  };

  // Cover
  doc.setFillColor(5, 5, 5);
  doc.rect(0, 0, W, 297, "F");
  const hero = await loadImg("/images/hero.png");
  doc.addImage(hero, imgFmt(hero), M, 105, W - 2 * M, 88);
  doc.setFont("courier", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...RED);
  doc.text(sanitize(t.hero.overline).toUpperCase(), M, 42);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(42);
  doc.setTextColor(255, 255, 255);
  doc.text("SHADOW", M, 62);
  doc.text("SHIELD.", M, 77);
  doc.setFont("courier", "normal");
  doc.setFontSize(9);
  doc.setTextColor(150, 150, 150);
  doc.text(sanitize(t.hero.sub), M, 90, { maxWidth: W - 2 * M });
  doc.text(new Date().toLocaleDateString(lang === "it" ? "it-IT" : "en-GB"), M, 285);
  doc.addPage();
  y = 20;

  heading(`${t.stealth.index} ${t.stealth.title}`);
  para(t.stealth.lead);
  await image("/images/material_macro.png", 70);
  t.stealth.features.forEach((f) => {
    sub(f.t);
    para(f.d);
  });

  heading(`${t.lines.index} ${t.lines.title}`);
  para(t.lines.lead);
  for (const item of t.lines.items) {
    sub(`${item.tag} — ${item.t}`);
    await image(item.img, 62);
    bullets(item.specs);
    y += 2;
  }

  heading(`${t.alarm.index} ${t.alarm.title}`);
  para(t.alarm.lead);
  t.alarm.points.forEach((p) => {
    sub(p.t);
    para(p.d);
  });

  heading(`${t.verification.index} ${t.verification.title}`);
  para(t.verification.lead);
  table(t.verification.table_head, t.verification.table_rows, [34, 28, 28, 28, 62]);
  sub(t.verification.strengths_t);
  bullets(t.verification.strengths);
  sub(t.verification.weak_t);
  bullets(t.verification.weak);
  sub(t.verification.verdict_t);
  para(t.verification.verdict, 9, [20, 20, 20]);

  heading(`${t.lifecycle.index} ${t.lifecycle.title}`);
  para(t.lifecycle.lead);
  table(t.lifecycle.table_head, t.lifecycle.table_rows, [120, 60]);
  para(t.lifecycle.note);

  checkPage(20);
  doc.setFont("courier", "normal");
  doc.setFontSize(8);
  doc.setTextColor(140, 140, 140);
  doc.text(sanitize(t.footer), M, y + 4);

  doc.save(`shadow-shield-dossier-${lang}.pdf`);
};
